import { DateTime, Interval } from "luxon";
import { YEAR_MONTH_DAY_FORMAT } from "@holisto-web/date-formats";
import { formatDate } from "./format-date.js";

type Digit = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
type Year = `19${Digit}${Digit}` | `20${Digit}${Digit}`;
type Month = `0${Exclude<Digit, 0>}` | `1${0 | 1 | 2}`;
type Day = `0${Exclude<Digit, 0>}` | `${1 | 2}${Digit}` | `3${0 | 1}`;

export type DateOnly = `${Year}-${Month}-${Day}`;

export type DateOnlyRange = {
  start: DateOnly | null;
  end: DateOnly | null;
};

export type DateOnlySelectedRange = {
  start: DateOnly;
  end: DateOnly;
};

export const dateTimeToDateOnly = (dateTime: DateTime): DateOnly => {
  return formatDate(dateTime, "en", YEAR_MONTH_DAY_FORMAT) as DateOnly;
};

export const dateOnlyToDateTime = (dateOnly: DateOnly): DateTime => {
  return DateTime.fromFormat(dateOnly, YEAR_MONTH_DAY_FORMAT);
};

export const formatDateOnly = (dateOnly: DateOnly, locale: string, format: string): string => {
  return formatDate(dateOnlyToDateTime(dateOnly), locale, format);
};

export const getNightsNumberFromDates = (startDate?: DateOnly | null, endDate?: DateOnly | null): number => {
  if (!startDate || !endDate) {
    return 0;
  }

  return Interval.fromDateTimes(dateOnlyToDateTime(startDate), dateOnlyToDateTime(endDate)).length("days");
};

export const getDaysFromNow = (date: DateOnly): number => {
  const dateTime = dateOnlyToDateTime(date);
  const utcNow = DateTime.now().toUTC();

  if (dateTime < utcNow) {
    return 0;
  }

  return Math.abs(dateTime.endOf("day").diff(utcNow.startOf("day"), ["days", "hours"]).days);
};

export const CHECK_IN_ALLOWED_WINDOW_IN_DAYS = 450;

export const isCheckInDateWithinAllowedWindow = (
  checkIn: DateOnly,
  allowedWindowInDays: number = CHECK_IN_ALLOWED_WINDOW_IN_DAYS,
): boolean => {
  const parsedDate = dateOnlyToDateTime(checkIn);
  const checkInDate = parsedDate.startOf("day");
  const today = DateTime.now().startOf("day");
  const maxAllowedCheckInDate = today.plus({ days: allowedWindowInDays });
  const minAllowedCheckInDate = today.minus({ days: 1 });

  return parsedDate.isValid && checkInDate >= minAllowedCheckInDate && checkInDate <= maxAllowedCheckInDate;
};

export const shouldDisableCheckInDateOutsideAllowedWindow = (
  checkInDate: DateOnly,
  allowedWindowInDays: number = CHECK_IN_ALLOWED_WINDOW_IN_DAYS,
): boolean => {
  return !isCheckInDateWithinAllowedWindow(checkInDate, allowedWindowInDays);
};
